(() => {
    "use strict";

    const GROUP_SIZE = 3;
    const COMPACT_QUERY = "(max-width: 900px)";
    const DISPLAY_MS = 2750;
    const WIPE_MS = 1180;

    let pageSignal = null;
    let autoplayTimer = null;

    function escapeHtml(value) {
        const element = document.createElement("span");
        element.textContent = String(value ?? "");
        return element.innerHTML;
    }

    function readSpeakers() {
        const node = document.getElementById("site-hero-cards-data");
        if (!node?.textContent) {
            return [];
        }
        try {
            const parsed = JSON.parse(node.textContent);
            if (!Array.isArray(parsed)) {
                return [];
            }

            const seenIds = new Set();
            const seenUrls = new Set();
            const seenNames = new Set();

            return parsed.filter((item) => {
                if (!item?.src || !item?.url) {
                    return false;
                }

                const id = String(item.id || "").trim();
                const url = String(item.url).trim().replace(/[?#].*$/, "").replace(/\/$/, "").toLowerCase();
                const name = String(item.nome || "").trim().replace(/\s+/g, " ").toLocaleLowerCase("pt-BR");
                if ((id && seenIds.has(id)) || seenUrls.has(url) || (name && seenNames.has(name))) {
                    return false;
                }

                if (id) {
                    seenIds.add(id);
                }
                seenUrls.add(url);
                if (name) {
                    seenNames.add(name);
                }
                return true;
            });
        } catch (error) {
            return [];
        }
    }

    function prefersReducedMotion() {
        return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }

    function stopAutoplay() {
        if (autoplayTimer !== null) {
            window.clearInterval(autoplayTimer);
            autoplayTimer = null;
        }
    }

    function bindScrollHint(section, signal) {
        const hint = section.querySelector(".hero-scroll-hint");
        const next = section.parentElement?.querySelector(":scope > .site-section");
        if (!hint || !next) {
            return;
        }
        hint.addEventListener("click", () => {
            next.scrollIntoView({
                behavior: prefersReducedMotion() ? "auto" : "smooth",
                block: "start",
            });
        }, { signal });
    }

    function boot() {
        pageSignal?.abort();
        pageSignal = new AbortController();
        const { signal } = pageSignal;

        const section = document.getElementById("site-hero");
        if (!section?.classList.contains("site-hero--cinematic")) {
            return;
        }

        stopAutoplay();
        bindScrollHint(section, signal);

        const track = section.querySelector(".hero-cards-track");
        const segmentsRoot = section.querySelector(".hero-segments");
        const cardsRoot = section.querySelector(".hero-cards");
        if (!track || !segmentsRoot || !cardsRoot) {
            return;
        }

        const speakers = readSpeakers();
        if (!speakers.length) {
            return;
        }

        const compactQuery = window.matchMedia(COMPACT_QUERY);
        const preloadCache = new Map();
        const slotTimers = new WeakMap();

        function speakerKey(speaker) {
            return String(speaker?.id || speaker?.url || speaker?.nome || "").trim().toLocaleLowerCase("pt-BR");
        }

        function chunkSpeakers() {
            const chunks = [];
            for (let i = 0; i < speakers.length; i += GROUP_SIZE) {
                chunks.push(speakers.slice(i, i + GROUP_SIZE));
            }

            const last = chunks.at(-1);
            if (!last || last.length === GROUP_SIZE || chunks.length === 1) {
                return chunks;
            }

            const previous = chunks.at(-2) || [];
            const first = chunks[0] || [];
            const blockedAdjacent = new Set([...last, ...previous, ...first].map(speakerKey));
            const blockedPrevious = new Set([...last, ...previous].map(speakerKey));
            const selected = new Set(last.map(speakerKey));
            const pools = [
                speakers.filter((speaker) => !blockedAdjacent.has(speakerKey(speaker))),
                speakers.filter((speaker) => !blockedPrevious.has(speakerKey(speaker))),
                speakers,
            ];

            pools.some((pool) => {
                pool.forEach((speaker) => {
                    const key = speakerKey(speaker);
                    if (last.length < GROUP_SIZE && !selected.has(key)) {
                        last.push(speaker);
                        selected.add(key);
                    }
                });
                return last.length === GROUP_SIZE;
            });
            return chunks;
        }

        let groups = chunkSpeakers();
        let currentGroup = 0;
        let touchStartX = 0;
        let slots = [];

        function createCard(speaker, indexInGroup) {
            const card = document.createElement("a");
            card.className = "hero-card";
            card.style.setProperty("--card-delay", `${(GROUP_SIZE - indexInGroup - 1) * 90}ms`);
            fillCard(card, speaker, indexInGroup);
            return card;
        }

        function fillCard(card, speaker, indexInGroup) {
            const src = escapeHtml(speaker.src);
            const name = escapeHtml(speaker.nome);
            const bio = escapeHtml(speaker.bio);
            const photoClass = speaker.has_cutout ? "hero-card-photo is-active has-cutout" : "hero-card-photo is-active is-catalog-fallback";
            card.href = speaker.url || "#";
            card.dataset.speakerId = speaker.id ? String(speaker.id) : "";
            card.innerHTML = `
                <span class="hero-card-visual">
                    <span class="hero-card-stage">
                        <span class="hero-card-frame" aria-hidden="true"></span>
                        <img
                            class="${photoClass}"
                            src="${src}"
                            ${speaker.srcset ? `srcset="${escapeHtml(speaker.srcset)}" sizes="${escapeHtml(speaker.sizes)}"` : ""}
                            alt="${name}"
                            width="820"
                            height="1172"
                            loading="${indexInGroup === 0 ? "eager" : "lazy"}"
                            decoding="async"
                            ${indexInGroup === 0 ? 'fetchpriority="high"' : ""}
                        />
                    </span>
                    <span class="hero-card-copy">
                        <strong class="hero-card-name">${name}</strong>
                        ${bio ? `<span class="hero-card-bio">${bio}</span>` : ""}
                    </span>
                </span>
            `;
        }

        function ensurePhotoBuffer(card) {
            const stage = card.querySelector(".hero-card-stage");
            if (!stage || stage.querySelectorAll(".hero-card-photo").length >= 2) {
                return;
            }
            const buffer = document.createElement("img");
            buffer.className = "hero-card-photo";
            buffer.width = 820;
            buffer.height = 1172;
            buffer.alt = "";
            buffer.decoding = "async";
            buffer.setAttribute("aria-hidden", "true");
            buffer.style.opacity = "0";
            stage.appendChild(buffer);
        }

        function setCopy(card, speaker) {
            const nameNode = card.querySelector(".hero-card-name");
            let bioNode = card.querySelector(".hero-card-bio");
            const copy = card.querySelector(".hero-card-copy");
            if (nameNode) {
                nameNode.textContent = speaker.nome || "";
            }
            const bio = String(speaker.bio || "").trim();
            if (bio) {
                if (!bioNode && copy) {
                    bioNode = document.createElement("span");
                    bioNode.className = "hero-card-bio";
                    copy.appendChild(bioNode);
                }
                if (bioNode) {
                    bioNode.textContent = bio;
                }
            } else if (bioNode) {
                bioNode.remove();
            }
        }

        function applySpeaker(card, speaker) {
            card.href = speaker.url || "#";
            card.dataset.speakerId = speaker.id ? String(speaker.id) : "";
            setCopy(card, speaker);
        }

        function clearSlotTimer(card) {
            const timer = slotTimers.get(card);
            if (timer) {
                window.clearTimeout(timer);
                slotTimers.delete(card);
            }
            card.classList.remove("is-page-flipping", "is-slot-exiting");
            card.querySelectorAll(".hero-card-photo.is-page-wiping, .hero-card-photo.is-incoming").forEach((photo) => {
                photo.classList.remove("is-page-wiping", "is-incoming");
                photo.style.zIndex = "";
                photo.style.maskImage = "";
                photo.style.webkitMaskImage = "";
            });
        }

        function markPhotoActive(photo, speakerName) {
            photo.classList.add("is-active");
            photo.classList.remove("is-page-wiping", "is-incoming");
            photo.style.opacity = "1";
            photo.style.zIndex = "";
            photo.style.maskImage = "";
            photo.style.webkitMaskImage = "";
            photo.alt = speakerName || "";
            photo.removeAttribute("aria-hidden");
        }

        function markPhotoIdle(photo) {
            photo.classList.remove("is-active", "is-page-wiping", "is-incoming");
            photo.style.opacity = "0";
            photo.style.maskImage = "";
            photo.style.webkitMaskImage = "";
            photo.alt = "";
            photo.setAttribute("aria-hidden", "true");
            photo.style.zIndex = "";
        }

        function updateSlot(card, speaker, indexInGroup, animate) {
            ensurePhotoBuffer(card);
            card.hidden = false;
            card.style.setProperty("--card-delay", animate ? "0ms" : `${(GROUP_SIZE - indexInGroup - 1) * 90}ms`);

            const photos = [...card.querySelectorAll(".hero-card-photo")];
            const outgoing = photos.find((photo) => photo.classList.contains("is-active")) || photos[0];
            const incoming = photos.find((photo) => photo !== outgoing) || photos[1];
            if (!outgoing) {
                return;
            }

            const alreadyShowing = outgoing.getAttribute("src") === speaker.src;
            if (!animate || prefersReducedMotion() || alreadyShowing || !incoming) {
                clearSlotTimer(card);
                applySpeaker(card, speaker);
                outgoing.srcset = speaker.srcset || "";
                outgoing.sizes = speaker.sizes || "";
                outgoing.src = speaker.src;
                markPhotoActive(outgoing, speaker.nome);
                if (incoming) {
                    markPhotoIdle(incoming);
                }
                return;
            }

            clearSlotTimer(card);
            applySpeaker(card, speaker);
            incoming.alt = speaker.nome || "";
            incoming.removeAttribute("aria-hidden");
            incoming.srcset = speaker.srcset || "";
            incoming.sizes = speaker.sizes || "";
            incoming.src = speaker.src;

            return () => {
                incoming.classList.add("is-incoming");
                incoming.classList.remove("is-page-wiping");
                incoming.style.opacity = "1";
                incoming.style.zIndex = "3";

                outgoing.classList.remove("is-active", "is-incoming");
                outgoing.classList.add("is-page-wiping");
                outgoing.style.opacity = "1";
                outgoing.style.zIndex = "5";

                const finish = () => {
                    if (!outgoing.isConnected || !outgoing.classList.contains("is-page-wiping")) {
                        return;
                    }
                    markPhotoIdle(outgoing);
                    markPhotoActive(incoming, speaker.nome);
                    slotTimers.delete(card);
                };

                outgoing.addEventListener("animationend", (event) => {
                    if (event.animationName === "site-hero-photo-wipe") {
                        finish();
                    }
                }, { once: true });
                slotTimers.set(card, window.setTimeout(finish, WIPE_MS + 40));
            };
        }

        function ensureSlots(group) {
            slots = [...track.querySelectorAll(".hero-card")];
            if (slots.length >= GROUP_SIZE) {
                slots = slots.slice(0, GROUP_SIZE);
                slots.forEach((card) => ensurePhotoBuffer(card));
                return;
            }

            track.replaceChildren();
            slots = [];
            for (let index = 0; index < GROUP_SIZE; index += 1) {
                const speaker = group[index] || speakers[index % speakers.length];
                const card = createCard(speaker, index);
                if (!group[index]) {
                    card.hidden = true;
                }
                track.appendChild(card);
                ensurePhotoBuffer(card);
                slots.push(card);
            }
        }

        function renderGroup(groupIndex, { animate = false } = {}) {
            const group = groups[groupIndex];
            if (!group) {
                return;
            }

            ensureSlots(group);
            track.dataset.visibleCount = String(group.length);
            const wipes = [];
            slots.forEach((card, index) => {
                const speaker = group[index];
                if (!speaker) {
                    clearSlotTimer(card);
                    card.hidden = true;
                    return;
                }
                const startWipe = updateSlot(card, speaker, index, animate);
                if (typeof startWipe === "function") {
                    wipes.push(startWipe);
                }
            });
            if (wipes.length) {
                window.requestAnimationFrame(() => wipes.forEach((startWipe) => startWipe()));
            }

            segmentsRoot.querySelectorAll(".hero-segment").forEach((segment, index) => {
                const isActive = index === groupIndex;
                segment.classList.toggle("is-active", isActive);
                segment.setAttribute("aria-selected", isActive ? "true" : "false");
            });
        }

        function createSegments() {
            segmentsRoot.replaceChildren();
            groups.forEach((_, index) => {
                const button = document.createElement("button");
                button.type = "button";
                button.className = "hero-segment";
                button.setAttribute("role", "tab");
                button.setAttribute("aria-label", `Grupo ${index + 1} de palestrantes`);
                button.setAttribute("aria-selected", index === 0 ? "true" : "false");
                if (index === 0) {
                    button.classList.add("is-active");
                }
                button.addEventListener("click", () => goToGroup(index), { signal });
                segmentsRoot.appendChild(button);
            });
        }

        function isAutomatedClient() {
            return Boolean(navigator.webdriver)
                || /Chrome-Lighthouse|PageSpeed/i.test(navigator.userAgent);
        }

        function canAutoplay() {
            return groups.length > 1
                && !prefersReducedMotion()
                && !compactQuery.matches
                && !isAutomatedClient();
        }

        function restartAutoplay() {
            stopAutoplay();
            if (!canAutoplay()) {
                return;
            }

            const start = () => {
                if (autoplayTimer !== null || !canAutoplay()) {
                    return;
                }
                autoplayTimer = window.setInterval(() => goToGroup(currentGroup + 1), DISPLAY_MS);
            };

            // Não dispara no first paint: o Lighthouse (e o 4G lento) medem a
            // home parada. O próximo grupo só entra depois de idle + 4s.
            if (typeof window.requestIdleCallback === "function") {
                window.requestIdleCallback(() => window.setTimeout(start, 4000), { timeout: 8000 });
                return;
            }
            window.setTimeout(start, 6000);
        }

        function syncLayout() {
            groups = chunkSpeakers();
            currentGroup = Math.min(currentGroup, Math.max(groups.length - 1, 0));
            createSegments();
            renderGroup(currentGroup, { animate: false });
            restartAutoplay();
        }

        let transitionToken = 0;

        async function goToGroup(index) {
            const next = (index + groups.length) % groups.length;
            if (next === currentGroup) {
                return;
            }
            const token = ++transitionToken;
            stopAutoplay();
            await preloadGroup(next);
            if (token !== transitionToken) {
                return;
            }
            currentGroup = next;
            renderGroup(currentGroup, { animate: true });
            preloadGroup(currentGroup + 1);
            restartAutoplay();
        }

        function preloadImage(speaker) {
            const src = speaker?.src;
            if (!src) {
                return Promise.resolve();
            }
            if (preloadCache.has(src)) {
                return preloadCache.get(src);
            }
            const promise = new Promise((resolve) => {
                const image = new Image();
                let resolved = false;
                const done = () => {
                    if (resolved) {
                        return;
                    }
                    resolved = true;
                    resolve();
                };
                image.decoding = "async";
                image.addEventListener("load", () => {
                    if (typeof image.decode === "function") {
                        image.decode().then(done).catch(done);
                    } else {
                        done();
                    }
                }, { once: true });
                image.addEventListener("error", done, { once: true });
                image.srcset = speaker.srcset || "";
                image.sizes = speaker.sizes || "";
                image.src = src;
                if (image.complete) {
                    done();
                }
            });
            preloadCache.set(src, promise);
            return promise;
        }

        function preloadGroup(index) {
            const group = groups[(index + groups.length) % groups.length];
            return Promise.all((group || []).map(preloadImage));
        }

        cardsRoot.addEventListener("keydown", (event) => {
            if (event.key === "ArrowLeft") {
                event.preventDefault();
                goToGroup(currentGroup - 1);
            }
            if (event.key === "ArrowRight") {
                event.preventDefault();
                goToGroup(currentGroup + 1);
            }
        }, { signal });

        cardsRoot.addEventListener("touchstart", (event) => {
            touchStartX = event.changedTouches[0]?.clientX ?? 0;
        }, { passive: true, signal });

        cardsRoot.addEventListener("touchend", (event) => {
            const touchEndX = event.changedTouches[0]?.clientX ?? 0;
            const delta = touchEndX - touchStartX;
            if (Math.abs(delta) < 40) {
                return;
            }
            if (delta > 0) {
                goToGroup(currentGroup - 1);
            } else {
                goToGroup(currentGroup + 1);
            }
        }, { passive: true, signal });

        compactQuery.addEventListener("change", syncLayout, { signal });

        createSegments();
        renderGroup(0, { animate: false });
        restartAutoplay();
        cardsRoot.dataset.ready = "true";
    }

    window.siteReady(boot);
})();
